import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import MainLayout from "../../../components/layout/MainLayout";
import { getAllWorkTypes } from "../../../../Services/workTypeService";
import { getAllCompensationTypes } from "../../../../Services/compensationService";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { getAllUserByCreatedBy } from "../../../../Services/authService";
import { useSelector } from "react-redux";
import axios from "axios";
import { getUserTimeEntriesById } from "../../../../Services/timeEntryService";

export default function EditTimeEntryForm() {
  const { projectId, entryId } = useParams();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth.loggedInUser?._id);

  // Form state
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedScaffold, setSelectedScaffold] = useState(null);
  const [date, setDate] = useState(formatDate(new Date()));
  const [hours, setHours] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [selectedWorkType, setSelectedWorkType] = useState(null);
  const [selectedCompensation, setSelectedCompensation] = useState(null);
  const [comments, setComments] = useState("");
  const [approvalStatus, setApprovalStatus] = useState(null);

  // Data state
  const [timeEntryData, setTimeEntryData] = useState(null);
  const [workTypes, setWorkTypes] = useState([]);
  const [compensationTypes, setCompensationTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [availableScaffolds, setAvailableScaffolds] = useState([]);
  const [approvalStatuses] = useState([
    { id: 1, name: "Pending", value: "pending" },
    { id: 2, name: "Approved", value: "approved" },
    { id: 3, name: "Rejected", value: "rejected" },
  ]);
  const [error, setError] = useState("");

  // Loading states
  const [loading, setLoading] = useState({
    timeEntry: false,
    users: false,
    workTypes: false,
    compensationTypes: false,
    saveEntry: false,
  });

  // Dropout states
  const [usersDropdownOpen, setUsersDropdownOpen] = useState(false);
  const [scaffoldDropdownOpen, setScaffoldDropdownOpen] = useState(false);
  const [workTypeDropdownOpen, setWorkTypeDropdownOpen] = useState(false);
  const [compensationDropdownOpen, setCompensationDropdownOpen] = useState(false);
  const [approvalDropdownOpen, setApprovalDropdownOpen] = useState(false);

  function formatDate(date) {
    if (!date) return "";
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const fetchTimeEntryData = async () => {
    if (!entryId) return;

    try {
      setLoading((prev) => ({ ...prev, timeEntry: true }));
      const response = await getUserTimeEntriesById(entryId);
      
      if (response && response.success) {
        const entryData = response.data;
        setTimeEntryData(entryData);

        // Safely handle all data assignments with null checks
        if (entryData.userId) setSelectedUser(entryData.userId);
        if (entryData.projectId) setSelectedProject(entryData.projectId);
        if (entryData.scaffoldId) setSelectedScaffold(entryData.scaffoldId);
        if (entryData.date) setDate(formatDate(entryData.date));
        if (entryData.totalHours !== undefined && entryData.totalHours !== null) {
          setHours(entryData.totalHours.toString());
        }
        if (entryData.startTime) setStartTime(entryData.startTime);
        if (entryData.endTime) setEndTime(entryData.endTime);
        if (entryData.comments) setComments(entryData.comments);

        // Find the approval status
        if (entryData.approvalStatus) {
          const status = approvalStatuses.find(
            (s) => s.value === entryData.approvalStatus
          );
          if (status) setApprovalStatus(status);
        }

        // Handle scaffolds safely
        if (entryData.projectId && Array.isArray(entryData.projectId.scaffolds)) {
          setAvailableScaffolds(entryData.projectId.scaffolds);
        }
      } else {
        toast.error("Failed to load time entry data");
      }
    } catch (error) {
      console.error("Error fetching time entry:", error);
      toast.error(
        error.response?.data?.message || "Failed to load time entry data"
      );
      setError("Failed to load time entry data. Please try again later.");
    } finally {
      setLoading((prev) => ({ ...prev, timeEntry: false }));
    }
  };

  const getAllWorkTypesData = async () => {
    try {
      setLoading((prev) => ({ ...prev, workTypes: true }));
      const responseData = await getAllWorkTypes();
      if (responseData?.data && Array.isArray(responseData.data)) {
        setWorkTypes(responseData.data);
        
        // Set selected work type if timeEntryData exists
        if (timeEntryData && timeEntryData.workTypeId) {
          const workType = responseData.data.find(
            (wt) => wt._id === (typeof timeEntryData.workTypeId === 'object' ? 
              timeEntryData.workTypeId._id : timeEntryData.workTypeId)
          );
          if (workType) setSelectedWorkType(workType);
        } else if (responseData.data.length > 0) {
          setSelectedWorkType(responseData.data[0]);
        }
      } else {
        setWorkTypes([]);
      }
    } catch (error) {
      console.error("Error fetching work types:", error);
      toast.error(error.response?.data?.message || "Failed to load work types");
      setError("Failed to load work types. Please try again later.");
    } finally {
      setLoading((prev) => ({ ...prev, workTypes: false }));
    }
  };

  const getAllCompensationTypesData = async () => {
    try {
      setLoading((prev) => ({ ...prev, compensationTypes: true }));
      const responseData = await getAllCompensationTypes();
      if (responseData?.data && Array.isArray(responseData.data)) {
        setCompensationTypes(responseData.data);

        // Handle compensation type selection
        if (timeEntryData && timeEntryData.compensationTypeId) {
          const compType = responseData.data.find(
            (ct) => ct._id === (typeof timeEntryData.compensationTypeId === 'object' ? 
              timeEntryData.compensationTypeId._id : timeEntryData.compensationTypeId)
          );
          if (compType) setSelectedCompensation(compType);
        } else {
          const noneCompensation = responseData.data.find(
            (comp) => comp.name?.toLowerCase() === "none"
          );
          setSelectedCompensation(noneCompensation || (responseData.data.length > 0 ? responseData.data[0] : null));
        }
      } else {
        setCompensationTypes([]);
      }
    } catch (error) {
      console.error("Error fetching compensation types:", error);
      toast.error(
        error.response?.data?.message || "Failed to load compensation types"
      );
      setError("Failed to load compensation types. Please try again later.");
    } finally {
      setLoading((prev) => ({ ...prev, compensationTypes: false }));
    }
  };

  const getUserDetails = async () => {
    try {
      setLoading((prev) => ({ ...prev, users: true }));
      const dataResponse = await getAllUserByCreatedBy(userId);
      const userDataResponse = dataResponse?.data;
      setUsers(Array.isArray(userDataResponse) ? userDataResponse : []);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error(error.response?.data?.message || "Failed to load user data");
      setError("Failed to load user data. Please try again later.");
    } finally {
      setLoading((prev) => ({ ...prev, users: false }));
    }
  };

  useEffect(() => {
    if (userId) {
      fetchTimeEntryData();
      getUserDetails();
      getAllWorkTypesData();
      getAllCompensationTypesData();
    }
  }, [userId]);

  useEffect(() => {
    // Update work type when both timeEntryData and workTypes are available
    if (timeEntryData && workTypes.length > 0 && timeEntryData.workTypeId) {
      const workTypeId = typeof timeEntryData.workTypeId === 'object' ? 
        timeEntryData.workTypeId._id : timeEntryData.workTypeId;
        
      const workType = workTypes.find(wt => wt._id === workTypeId);
      if (workType) setSelectedWorkType(workType);
    }

    // Update compensation type when both timeEntryData and compensationTypes are available
    if (timeEntryData && compensationTypes.length > 0 && timeEntryData.compensationTypeId) {
      const compTypeId = typeof timeEntryData.compensationTypeId === 'object' ? 
        timeEntryData.compensationTypeId._id : timeEntryData.compensationTypeId;
        
      const compType = compensationTypes.find(ct => ct._id === compTypeId);
      if (compType) setSelectedCompensation(compType);
    }
  }, [timeEntryData, workTypes, compensationTypes]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      toast.error("Please select a user");
      return;
    }

    if (!selectedWorkType) {
      toast.error("Please select a work type");
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, saveEntry: true }));

      const entryData = {
        userId: selectedUser._id,
        projectId: selectedProject?._id,
        scaffoldId: selectedScaffold?._id,
        date,
        totalHours: parseFloat(hours) || 0,
        startTime,
        endTime,
        workTypeId: selectedWorkType?._id,
        compensationTypeId: selectedCompensation?._id,
        comments,
        approvalStatus: approvalStatus?.value || "pending",
      };

      const response = await axios.put(`/api/time-entries/${entryId}`, entryData);

      if (response.data.success) {
        toast.success("Time entry updated successfully");
        navigate(`/projects/${projectId}/time-entries`);
      } else {
        toast.error(response.data.message || "Failed to update time entry");
      }
    } catch (error) {
      console.error("Error updating time entry:", error);
      toast.error(
        error.response?.data?.message || "Failed to update time entry"
      );
    } finally {
      setLoading((prev) => ({ ...prev, saveEntry: false }));
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Safely get display name for various objects
  const getDisplayName = (obj) => {
    if (!obj) return "";
    return obj.name || obj.userName || obj.userEmail || obj.projectName || obj.scaffoldName || obj.workTypeName || obj.compensationTypeName || "";
  };



  return (
    <MainLayout title={"Edit Time Entry"}>
      <div className="mx-auto bg-white p-6">
        <h1 className="text-2xl font-medium text-gray-800 border-b pb-4 mb-6">
          Edit Time Entry
        </h1>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {loading.timeEntry ? (
          <div className="flex justify-center py-8">
            <p>Loading time entry data...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* User Field */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  User <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between border rounded px-3 py-2 text-left bg-gray-50"
                    onClick={() => setUsersDropdownOpen(!usersDropdownOpen)}
                    disabled={true}
                  >
                    {loading.users ? (
                      <span>Loading users...</span>
                    ) : selectedUser ? (
                      getDisplayName(selectedUser)
                    ) : (
                      "Select User"
                    )}
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    User cannot be changed
                  </p>

                  {usersDropdownOpen && !loading.users && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg">
                      <ul className="py-1 max-h-60 overflow-auto">
                        {users?.length > 0 ? (
                          users?.map((user) => (
                            <li
                              key={user._id}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setSelectedUser(user);
                                setUsersDropdownOpen(false);
                              }}
                            >
                              {getDisplayName(user)}
                            </li>
                          ))
                        ) : (
                          <li className="px-3 py-2 text-gray-500">
                            No users available
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Project Field - Read Only */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  Project
                </label>
                <input
                  type="text"
                  value={selectedProject ? getDisplayName(selectedProject) : ""}
                  className="w-full border rounded px-3 py-2 bg-gray-50"
                  disabled
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">
                  Project cannot be changed
                </p>
              </div>

              {/* Scaffold ID Field */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  Scaffold ID
                </label>
                <div className="relative">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between border rounded px-3 py-2 text-left bg-gray-50"
                    onClick={() => setScaffoldDropdownOpen(!scaffoldDropdownOpen)}
                    disabled={true}
                  >
                    {selectedScaffold ? getDisplayName(selectedScaffold) : "Select Scaffold ID"}
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    Scaffold cannot be changed
                  </p>

                  {scaffoldDropdownOpen && availableScaffolds.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg">
                      <ul className="py-1 max-h-60 overflow-auto">
                        {availableScaffolds?.map((scaffold) => (
                          <li
                            key={scaffold._id}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setSelectedScaffold(scaffold);
                              setScaffoldDropdownOpen(false);
                            }}
                          >
                            {getDisplayName(scaffold)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
             
              </div>

              {/* Date Field */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>

              {/* Hours Field */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  Hours <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  step="0.25"
                  min="0"
                  required
                />
              </div>

              {/* Start Time Field */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  Start Time
                </label>
                <input
                  type="time"
                  value={startTime || ""}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              {/* End Time Field */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  End Time
                </label>
                <input
                  type="time"
                  value={endTime || ""}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              {/* Work Type Field */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  Work Type <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between border rounded px-3 py-2 text-left"
                    onClick={() => setWorkTypeDropdownOpen(!workTypeDropdownOpen)}
                    disabled={loading.workTypes}
                  >
                    {loading.workTypes ? (
                      <span>Loading work types...</span>
                    ) : selectedWorkType ? (
                      getDisplayName(selectedWorkType)
                    ) : (
                      "Select Work Type"
                    )}
                    <ChevronDown className="h-4 w-4" />
                  </button>

                  {workTypeDropdownOpen && !loading.workTypes && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg">
                      <ul className="py-1 max-h-60 overflow-auto">
                        {workTypes?.length > 0 ? (
                          workTypes?.map((workType) => (
                            <li
                              key={workType._id}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setSelectedWorkType(workType);
                                setWorkTypeDropdownOpen(false);
                              }}
                            >
                              {getDisplayName(workType)}
                            </li>
                          ))
                        ) : (
                          <li className="px-3 py-2 text-gray-500">
                            No work types available
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Compensation Field */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  Additional Compensation
                </label>
                <div className="relative">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between border rounded px-3 py-2 text-left"
                    onClick={() => setCompensationDropdownOpen(!compensationDropdownOpen)}
                    disabled={loading.compensationTypes}
                  >
                    {loading.compensationTypes ? (
                      <span>Loading compensation types...</span>
                    ) : selectedCompensation ? (
                      getDisplayName(selectedCompensation)
                    ) : (
                      "Select Compensation"
                    )}
                    <ChevronDown className="h-4 w-4" />
                  </button>

                  {compensationDropdownOpen && !loading.compensationTypes && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg">
                      <ul className="py-1 max-h-60 overflow-auto">
                        {compensationTypes?.length > 0 ? (
                          compensationTypes?.map((comp) => (
                            <li
                              key={comp._id}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setSelectedCompensation(comp);
                                setCompensationDropdownOpen(false);
                              }}
                            >
                              {getDisplayName(comp)}
                            </li>
                          ))
                        ) : (
                          <li className="px-3 py-2 text-gray-500">
                            No compensation types available
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Comments Field - Full Width */}
              <div className="md:col-span-2">
                <label className="block mb-1 text-sm font-medium">
                  Comments
                </label>
                <textarea
                  value={comments || ""}
                  onChange={(e) => setComments(e.target.value)}
                  className="w-full border rounded px-3 py-2 h-32"
                  placeholder="Enter any additional comments here..."
                />
              </div>

              {/* Approval Status Field */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  Approval Status
                </label>
                <div className="relative">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between border rounded px-3 py-2 text-left"
                    onClick={() => setApprovalDropdownOpen(!approvalDropdownOpen)}
                  >
                    {approvalStatus ? approvalStatus.name : "Select Status"}
                    <ChevronDown className="h-4 w-4" />
                  </button>

                  {approvalDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg">
                      <ul className="py-1 max-h-60 overflow-auto">
                        {approvalStatuses?.map((status) => (
                          <li
                            key={status.id}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setApprovalStatus(status);
                              setApprovalDropdownOpen(false);
                            }}
                          >
                            {status.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Buttons - Right Aligned */}
            <div className="flex justify-end gap-3 mt-8">
              <button
                type="button"
                className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-50"
                onClick={handleCancel}
                disabled={loading.saveEntry}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
                disabled={loading.saveEntry}
              >
                {loading.saveEntry ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        )}
      </div>
    </MainLayout>
  );
}